import { createSwiper } from './swiper';

const initSection = (section) => {
  const swiperInstances = new Map();

  const destroy = () => {
    swiperInstances.forEach((instance) => instance.destroy(true, true));
    swiperInstances.clear();
    const tabsContainer = section.querySelector(
      '.tabbed-product-carousel__tabs'
    );
    if (tabsContainer) tabsContainer.innerHTML = '';
    delete section.dataset.tabsInitialized;
  };

  if (section.dataset.tabsInitialized === 'true') return;
  section.dataset.tabsInitialized = 'true';

  const panels = Array.from(
    section.querySelectorAll('.tabbed-product-carousel__panel')
  );
  const tabsContainer = section.querySelector(
    '.tabbed-product-carousel__tabs'
  );

  if (tabsContainer && panels.length && !tabsContainer.children.length) {
    panels.forEach((panel, index) => {
      const tab = document.createElement('button');
      const tabLabel = panel.dataset.tabTitle || `Tab ${index + 1}`;
      const panelId = panel.id;
      const tabId = panel.getAttribute('aria-labelledby');

      tab.className = 'tabbed-product-carousel__tab';
      tab.id = tabId;
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', 'false');
      tab.tabIndex = -1;
      tab.textContent = tabLabel;

      tabsContainer.appendChild(tab);
    });
  }

  const tabs = Array.from(
    section.querySelectorAll('.tabbed-product-carousel__tab')
  );

  if (!tabs.length || !panels.length) return;

  section.style.setProperty('--tab-count', tabs.length);

  const initSwiper = (panel) => {
    const carousel = panel.querySelector(
      '.tabbed-product-carousel__carousel'
    );
    if (!carousel || swiperInstances.has(carousel)) return;

    const hasColorCards = panel.querySelector(
      '.tabbed-product-carousel__color-card'
    );
    const hasProductCards = panel.querySelector(
      '.tabbed-product-carousel__product'
    );

    let mobileGap = 8;
    let desktopGap = 16;
    if (hasColorCards) {
      mobileGap = 8;
      desktopGap = 12;
    } else if (hasProductCards) {
      mobileGap = 12;
      desktopGap = 16;
    }

    const instance = createSwiper(carousel, {
      lazy: true,
      slidesPerView: 'auto',
      spaceBetween: mobileGap,
      slideClass: 'swiper__col',
      wrapperClass: 'swiper__row',
      navigation: {
        nextEl: carousel.querySelector(
          '.tabbed-product-carousel__nav-btn--next'
        ),
        prevEl: carousel.querySelector(
          '.tabbed-product-carousel__nav-btn--prev'
        ),
      },
      breakpoints: {
        768: {
          spaceBetween: desktopGap,
        },
      },
      a11y: { enabled: true },
    });

    swiperInstances.set(carousel, instance);
  };

  const setActiveTab = (index) => {
    tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    initSwiper(panels[index]);
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => setActiveTab(index));
  });

  tabsContainer.addEventListener('keydown', (e) => {
    const current = tabs.indexOf(document.activeElement);
    if (current < 0) return;

    let next;
    if (e.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (e.key === 'ArrowLeft')
      next = (current - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;

    e.preventDefault();
    setActiveTab(next);
    tabs[next].focus();
  });

  setActiveTab(0);

  return destroy;
};

const tabbedProductCarousel = () => {
  const destroyers = new Map();

  const initAll = () => {
    document.querySelectorAll('.tabbed-product-carousel').forEach((section) => {
      const destroy = initSection(section);
      if (destroy) destroyers.set(section.id, destroy);
    });
  };

  initAll();

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.tabbed-product-carousel');
    if (section) {
      const destroy = initSection(section);
      if (destroy) destroyers.set(section.id, destroy);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('.tabbed-product-carousel');
    if (section && destroyers.has(section.id)) {
      destroyers.get(section.id)();
      destroyers.delete(section.id);
    }
  });

  document.addEventListener('shopify:block:select', (e) => {
    const panel = e.target.closest('.tabbed-product-carousel__panel');
    if (!panel) return;
    const section = panel.closest('.tabbed-product-carousel');
    if (!section) return;

    const panels = Array.from(
      section.querySelectorAll('.tabbed-product-carousel__panel')
    );
    const index = panels.indexOf(panel);
    if (index < 0) return;

    const tabs = Array.from(
      section.querySelectorAll('.tabbed-product-carousel__tab')
    );
    tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    panels.forEach((p, i) => {
      p.hidden = i !== index;
    });
  });
};

export default tabbedProductCarousel;
